import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'

/**
 * The political register's four screens.
 *
 * ## Four tabs, no groups
 *
 * The Polling Studio groups its thirteen screens because thirteen undifferentiated
 * tabs is a menu rather than an information architecture. Four is not thirteen: a
 * reader can see all of them at once, and a group label above four items would be
 * chrome that answers no question.
 *
 * ## Why the strip is here and not in the shell
 *
 * `AppShell` takes only `{ children }`; its sub-bar is hardcoded chrome with no
 * slot, portal or context, and the sidebar is one level deep. So the strip lives
 * in this layout, registered in `router.tsx` as a parent route with an index
 * child — the `PollingLayout` pattern, unchanged.
 *
 * ## No per-tab permission
 *
 * All four screens read the same authority, `politics:read`, and each one renders
 * its own `Forbidden` when the caller lacks it. Filtering tabs here would hide a
 * screen the caller may in fact be allowed to open, and a hidden control cannot be
 * told apart from a broken one.
 */

const TABS: { to: string; label: string; hint: string; end?: boolean }[] = [
  {
    to: '/political-landscape', label: 'Overview', end: true,
    hint: 'The register at a glance — who is registered, aligned and standing',
  },
  { to: '/political-landscape/parties', label: 'Parties', hint: 'The party register and its coalition memberships' },
  { to: '/political-landscape/coalitions', label: 'Coalitions', hint: 'Alliances, their member parties and their candidates' },
  { to: '/political-landscape/candidates', label: 'Candidates', hint: 'Who is standing, for what seat, where' },
]

export function PoliticsLayout() {
  // Filters live in the query string, and moving between tabs must carry them:
  // "Nairobi, GOVERNOR" silently becoming "everything" between two screens is how
  // two views end up disagreeing for a reason nothing on the page explains.
  const { search } = useLocation()

  return (
    <div className="space-y-4">
      <nav aria-label="Political landscape" className="axis-table-scroll border-b border-border">
        <ul className="flex items-stretch gap-1 min-w-max">
          {TABS.map((tab) => (
            <li key={tab.to}>
              <NavLink
                to={{ pathname: tab.to, search }}
                // `end` only on the index tab. Without it Overview stays lit on
                // every child route, which reads as two tabs selected at once.
                end={tab.end}
                title={tab.hint}
                className={({ isActive }) => cn(
                  'inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium border-b-2 -mb-px transition-colors',
                  isActive
                    ? 'border-chocolate text-chocolate'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Outlet />
    </div>
  )
}
